import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Divider,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";

function ComplaintModalLayout({
  open,
  onClose,
  title,
  subtitle,
  steps,
  activeStep,
  children,
  onBack,
  onNext,
  nextLabel = "Continue",
  backLabel = "Back",
  isLastStep = false,
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: "720px",
          maxHeight: "90vh",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          px: 4,
          py: 2.5,
          background: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(25, 118, 210, 0.10)",
              }}
            >
              <ReportProblemRoundedIcon
                sx={{
                  fontSize: 27,
                  color: "primary.main",
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
              >
                {title}
              </Typography>

              {subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            disabled={loading}
            sx={{
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* STEPPER */}
      <Box
        sx={{
          px: 5,
          py: 2.5,
          background: "#fafbfc",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          {steps.map((step, index) => {
            const completed = index < activeStep;
            const active = index === activeStep;

            return (
              <React.Fragment key={step.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      minWidth: 34,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      backgroundColor:
                        active || completed ? "primary.main" : "#e4e7eb",
                      color: active || completed ? "#fff" : "text.secondary",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {completed ? "✓" : index + 1}
                  </Box>

                  <Typography
                    variant="body2"
                    fontWeight={active ? 700 : 500}
                    color={
                      active
                        ? "primary.main"
                        : completed
                          ? "text.primary"
                          : "text.secondary"
                    }
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>

                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      mx: 2,
                      backgroundColor:
                        index < activeStep ? "primary.main" : "#e4e7eb",
                      transition: "all 0.2s ease",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <Divider />

      {/* CONTENT */}
      <DialogContent
        sx={{
          px: 5,
          py: 4,
          overflowY: "auto",
          background: "#fff",
        }}
      >
        {children}
      </DialogContent>

      <Divider />

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 5,
          py: 2,
          background: "#fff",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="text"
          onClick={onBack}
          disabled={activeStep === 0 || loading}
          sx={{
            minWidth: 100,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {backLabel}
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          disabled={loading}
          sx={{
            minWidth: 130,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: "none",
          }}
        >
          {isLastStep ? "Send complaint" : nextLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ComplaintModalLayout;
